/** API dei run agentici (SPEC-025, F-143/F-144). Sola lettura dal frontend, per ora. */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type AgentRunStep = {
  stepNo: number;
  /** `model_call`, `tool_call`, `gate_block`, `conclusion`, … (agent_run_steps.kind). */
  kind: string;
  toolName: string | null;
  input: unknown;
  output: unknown;
  ok: boolean | null;
  errorCode: string | null;
  durationMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  createdAt: string;
};

export type AgentRunProposal = {
  id: string;
  side: string;
  status: string;
  instrumentId: string;
  portfolioId: string;
  proposedAmount: string | null;
};

export type AgentRun = {
  id: string;
  purpose: string;
  question: string;
  status: string;
  terminatedReason: string | null;
  createdBy: { kind: string; sourceId: string | null; detail: string | null };
  mandate: unknown;
  mandateHash: string;
  llmTier: string;
  llmModel: string | null;
  counts: {
    steps: number;
    toolCalls: number;
    gateBlocks: number;
    proposals: number;
    wishlistItems: number;
    notifications: number;
  };
  proposalsAmountBase: string;
  notes: unknown;
  scopeInstrumentIds: string[] | null;
  tokens: { prompt: number | null; completion: number | null };
  costUsd: string | null;
  conclusion: unknown;
  conclusionRaw: string | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  steps?: AgentRunStep[];
  proposals?: AgentRunProposal[];
};

export const agentRunsKeys = {
  detail: (runId: string) => ['agent-runs', 'detail', runId] as const,
};

export function useAgentRun(runId: string | null) {
  return useQuery({
    queryKey: agentRunsKeys.detail(runId ?? ''),
    queryFn: ({ signal }) => api.get<AgentRun>(`/agent-runs/${runId!}`, signal),
    enabled: runId !== null,
    // Un run in corso cambia da solo: la pagina si aggiorna finche' non finisce.
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 5_000 : false),
  });
}
